import { CommentAltIcon } from "@xola/icons";
import { Button, flash, Skeleton } from "@xola/ui-kit";
import clsx from "clsx";
import { DateFormat, formatDate } from "common";
import React, { useState } from "react";
import { useMutation } from "react-query";
import { Conversation, ExperiencePurchaseItem, Message, Purchase, PurchaseObject, User } from "types";
import { api } from "../../api/api";
import { usePurchase } from "../../hooks/resources/usePurchase";
import { useSeller, useUser } from "../../hooks/useAccount";
import { useInvalidate } from "../../hooks/useInvalidation";
import { NoTranslate } from "../Common/NoTranslate";
import { SecondaryButton } from "../Common/SecondaryButton";
import { MessagePurchaseModal } from "../PurchaseModification/MessagePurchaseModal";
import { NotificationPurchaseTile } from "./NotificationPurchaseTile";
import { useResource } from "@/hooks/useResource";
import { isSent } from "@/models/Message";
import { getStrippedHtml } from "@/utils/getStrippedHtml";

interface UnreadMessageProps {
    conversation: Conversation;
    onDismiss: (conversationId: ID) => void;
    onClose: () => void;
}

export const UnreadMessage = ({ conversation, onDismiss, onClose }: UnreadMessageProps) => {
    const seller = useSeller();
    const user = useUser();
    const invalidate = useInvalidate();
    const [isOpen, setIsOpen] = useState(false);
    const { purchase, isLoadingPurchase } = usePurchase(conversation.purchase?.id, null, {
        enabled: !!conversation.purchase && (conversation.purchase as Purchase).object !== PurchaseObject.WAITLIST,
    });
    const message = conversation.messages?.at(-1);
    const { data: messageUser, isLoading: isLoadingUser } = useResource<User>(["users", message?.from]);

    const item = (purchase?.items.find((item) => item.id === message?.itemId) ??
        purchase?.items[0]) as ExperiencePurchaseItem;

    const { mutateAsync: dismissConversation, isLoading: isDismissing } = useMutation(
        async (participantId: string | undefined) => {
            if (!participantId) {
                return;
            }

            await api.delete(`conversations/${conversation.id}/participants/${participantId}/tags/unread`);
        },
    );

    const handleDismiss = async () => {
        const participantId = seller.id ?? message?.to?.find((to) => to.tags?.includes("unread"))?.id;
        await dismissConversation(participantId);
        if (participantId) {
            onDismiss(conversation.id);
        } else {
            flash.warning("Unable to dismiss message");
        }

        invalidate("conversations", conversation.id);
    };

    const handleMessageModalClose = () => {
        setIsOpen(false);
        onClose();
    };

    if (isLoadingPurchase || isLoadingUser) {
        return (
            <Skeleton height={100} classNames={{ container: "my-8 mr-3" }}>
                <div className="mr-3 h-14 w-14 rounded" />
            </Skeleton>
        );
    }

    const isSentMessage = message && isSent({ message, seller, user });
    const messageCreatedAt = message ? getMessageCreatedAt(message) : null;

    return purchase ? (
        <div className="pt-8" data-testid="unread-message-block">
            <div className="pb-2">
                <b className="pr-2">Unread Message</b>
                {messageCreatedAt && <NoTranslate>{messageCreatedAt}</NoTranslate>}
            </div>
            <NotificationPurchaseTile purchase={purchase} paymentTitle="Total Value" amount={purchase.amount} />
            <div className="mb-2 inline-flex rounded bg-gray-lighter p-1" data-testid="unread-message-from-label">
                From: <NoTranslate>{messageUser?.name}</NoTranslate>
            </div>
            <div
                data-testid="unread-message-text"
                className={clsx(
                    "no-translate rounded border border-gray-light bg-gray-light p-3",
                    isSentMessage ? null : "bg-blue-lighter",
                )}
            >
                {getStrippedHtml(message?.formats?.find((f) => f.type === "email" || f.type === "text")?.body ?? "")}
            </div>
            <div className={clsx("mt-2 flex", isSentMessage ? "justify-end" : "justify-between")}>
                {isSentMessage ? null : (
                    <Button size="small" icon={<CommentAltIcon />} onClick={() => setIsOpen(true)}>
                        Respond
                    </Button>
                )}
                <SecondaryButton disabled={isDismissing} size="small" onClick={handleDismiss}>
                    Dismiss
                </SecondaryButton>
            </div>
            <MessagePurchaseModal
                purchase={purchase}
                item={item}
                isOpen={isOpen}
                to={message?.from ? [{ id: message.from, type: "traveler" }] : undefined}
                preferredChannels={message?.channels}
                description="This message will be sent to the traveler"
                onClose={handleMessageModalClose}
                onContinue={handleMessageModalClose}
            />
        </div>
    ) : null;
};

const getMessageCreatedAt = (message: Message) => {
    if (!message.createdAt && !message.id) {
        return null;
    }

    if (message.createdAt) {
        return formatDate(message.createdAt, DateFormat.DATE);
    }

    if (message.id) {
        const timestamp = Number.parseInt(message.id.slice(0, 8), 16);
        return formatDate(timestamp * 1000, DateFormat.DATE);
    }

    return null;
};
